import React, { useMemo, useState } from 'react';
import { Alert, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import type { SocialDelivery } from '@modules/shared-types';
import { SocialEngagementService } from '../../services/SocialEngagementService';

export interface XManualPublicationPanelProps {
  projectId: string;
  strategyId: string;
  publicationId: string;
  scheduledAt?: string;
  delivery?: SocialDelivery;
  disabled?: boolean;
}

export const XManualPublicationPanel: React.FC<XManualPublicationPanelProps> = ({
  projectId,
  strategyId,
  publicationId,
  scheduledAt,
  delivery,
  disabled,
}) => {
  const service = useMemo(() => new SocialEngagementService(), []);
  const [state, setState] = useState<SocialDelivery | undefined>(delivery);
  const [preview, setPreview] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try { await action(); } catch (err) { setError(err instanceof Error ? err.message : 'X publication operation failed'); } finally { setBusy(false); }
  };

  const approve = () => run(async () => {
    const result = await service.approvePublication(projectId, strategyId, publicationId) as any;
    setState((current) => ({
      provider: 'x-twitter',
      mode: 'manual',
      ...(result.outbound?.replyToPostId ? { replyToExternalPostId: result.outbound.replyToPostId } : {}),
      approval: result.approval,
      publish: current?.publish ?? { status: 'not_sent' },
      latestMetrics: current?.latestMetrics,
    }));
    setPreview(null);
  });

  const dryRun = () => run(async () => {
    setPreview(await service.publishPublication(projectId, strategyId, publicationId, { dryRun: true, maxCostUsd: 0.25 }));
  });

  const send = () => run(async () => {
    const result = await service.publishPublication(projectId, strategyId, publicationId, { confirm: preview.confirmation, maxCostUsd: 0.25 }) as any;
    setState((current) => ({
      provider: 'x-twitter',
      mode: 'manual',
      approval: current?.approval ?? { status: 'approved' },
      replyToExternalPostId: current?.replyToExternalPostId,
      publish: {
        status: 'published',
        externalPostId: result.data?.externalPostId,
        externalPostUrl: result.data?.externalPostUrl,
        publishedAt: result.data?.createdAt,
        checkedAt: result.data?.createdAt,
        lastAttemptId: result.attemptId,
      },
      latestMetrics: current?.latestMetrics,
    }));
    setPreview(null);
  });

  const reconcile = () => run(async () => {
    const result = await service.reconcilePublication(projectId, strategyId, publicationId, { maxCostUsd: 0.25 }) as any;
    setState((current) => current ? {
      ...current,
      publish: result.matched
        ? { status: 'published', externalPostId: result.post?.id, externalPostUrl: result.post?.url, checkedAt: new Date().toISOString() }
        : { status: 'failed', checkedAt: new Date().toISOString(), errorCode: 'reconciliation_not_found' },
    } : current);
  });

  const analytics = () => run(async () => {
    const result = await service.refreshPublicationAnalytics(projectId, strategyId, publicationId, { maxCostUsd: 0.25 }) as any;
    const metrics = result.data?.snapshots?.[0]?.metrics;
    if (metrics) setState((current) => current ? { ...current, latestMetrics: metrics } : current);
  });

  const publishState = state?.publish?.status ?? 'not_sent';
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="x-manual-publication-panel">
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" fontWeight={600}>Manual X delivery</Typography>
          <Stack direction="row" spacing={1}><Chip size="small" label={`approval: ${state?.approval?.status ?? 'pending'}`} /><Chip size="small" label={publishState} /></Stack>
        </Stack>
        <Alert severity="info">
          Editorial target: {scheduledAt ? new Date(scheduledAt).toLocaleString() : 'not set'}. Reaching this time never sends anything to X.
        </Alert>
        {error && <Alert severity="error">{error}</Alert>}
        {preview && (
          <Alert data-testid="x-publication-send-preview" severity="warning" action={<Button data-testid="x-publication-send-confirm" color="inherit" size="small" startIcon={<SendIcon />} onClick={send}>Send now</Button>}>
            Exact outbound text: {preview.outbound?.text}{preview.outbound?.replyToPostId ? ` Reply target: ${preview.outbound.replyToPostId}.` : ''} This publishes immediately.
          </Alert>
        )}
        {state?.publish?.errorMessage && <Alert severity={publishState === 'needs_reconciliation' ? 'warning' : 'error'}>{state.publish.errorMessage}</Alert>}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button data-testid="x-publication-approve" size="small" variant="outlined" startIcon={<CheckCircleOutlineIcon />} disabled={busy || disabled} onClick={approve}>Approve current text</Button>
          <Button data-testid="x-publication-preview-send" size="small" variant="contained" color="warning" startIcon={<SendIcon />} disabled={busy || disabled || state?.approval?.status !== 'approved' || publishState === 'published'} onClick={dryRun}>Preview send</Button>
          {publishState === 'needs_reconciliation' && <Button data-testid="x-publication-reconcile" size="small" startIcon={<RefreshIcon />} disabled={busy || disabled} onClick={reconcile}>Reconcile</Button>}
          {publishState === 'published' && <Button data-testid="x-publication-analytics" size="small" startIcon={<RefreshIcon />} disabled={busy || disabled} onClick={analytics}>Refresh analytics</Button>}
          {state?.publish?.externalPostUrl && <Button size="small" href={state.publish.externalPostUrl} target="_blank" rel="noreferrer">Open in X</Button>}
        </Stack>
        {state?.latestMetrics && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {(['impressions', 'engagements', 'likes', 'replies', 'reposts', 'bookmarks'] as const).map((key) => state.latestMetrics?.[key] !== undefined ? <Chip key={key} size="small" variant="outlined" label={`${key}: ${state.latestMetrics[key]}`} /> : null)}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default XManualPublicationPanel;
